import React, { useEffect, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { FormikHelpers, useFormik } from "formik"
import DataTableTemplate from "Components/templates/data-table-template"
import Modal from "Components/molecule/modal"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import ActionButton from "Components/atoms/buttons/action-button"
import IconButton from "Components/atoms/buttons/icon-button"
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container"
import BillabilityUtilizationTargetForm from "Components/organisms/forms/billability-utilization-target-form"
import BillabilityUtilizationTargetFields from "Components/organisms/forms/billability-utilization-target-form/types"
import { BillabilityUtilizationTargetFormValidation } from "data/billability-utilization-target-form"
import { BillabilityUtilizationTarget } from "models/billability-utilization-target"
import {
    AdminBillabilityUtilizationTargetsSliceState,
    setTargets,
    addTarget,
    setTarget,
    removeTarget,
    setEditingTarget
} from "slices/admin/billability-utilization-targets/reducer"
import {
    getBillabilityUtilizationTargets,
    submitCreateBillabilityUtilizationTargetForm,
    submitEditBillabilityUtilizationTargetForm,
    deleteBillabilityUtilizationTargetRequest
} from "slices/admin/billability-utilization-targets/thunk"
import { PlusIcon, PencilIcon, TrashIcon, HelpIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"
import useFiscalYearOptions from "hooks/useFiscalYearOptions"


const selectTargets = createSelector(
    (state: any) => state.AdminBillabilityUtilizationTargets,
    ({ targets, editingTarget }: AdminBillabilityUtilizationTargetsSliceState) => ({
        targets,
        editingTarget
    })
)

const EMPTY_FORM: BillabilityUtilizationTargetFields = {
    fiscalYear: '',
    utilizationTarget: '',
    actualBillableTarget: '',
    leadsTarget: '',
    principalsTarget: '',
}

const fmtPct = (value: number) =>
    `${value.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`

const BillabilityUtilizationTargetsPage = () => {
    document.title = "Billability / Utilization Targets | IbexIQ";
    const { handleOpenManual } = useManual("admin/billability-utilization-targets");
    const dispatch: any = useDispatch()
    const { targets, editingTarget } = useSelector(selectTargets)

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [showFormModal, setShowFormModal] = useState<boolean>(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
    const [focusedTarget, setFocusedTarget] = useState<BillabilityUtilizationTarget | null>(null)
    const [isDeleting, setIsDeleting] = useState<boolean>(false)

    useEffect(() => {
        const init = async () => {
            const res = await getBillabilityUtilizationTargets()
            if (res) dispatch(setTargets(res))
            setIsLoading(false)
        }
        init()
    }, [])

    const handleCloseFormModal = () => {
        setShowFormModal(false)
        dispatch(setEditingTarget(null))
        resetForm()
    }

    const onSubmit = async (
        vals: BillabilityUtilizationTargetFields,
        helpers: FormikHelpers<BillabilityUtilizationTargetFields>
    ) => {
        helpers.setSubmitting(true)

        if (editingTarget) {
            const res = await submitEditBillabilityUtilizationTargetForm(editingTarget.id, vals)
            if (res) {
                dispatch(setTarget(res))
                handleCloseFormModal()
            }
        } else {
            const res = await submitCreateBillabilityUtilizationTargetForm(vals)
            if (res) {
                dispatch(addTarget(res))
                handleCloseFormModal()
            }
        }

        helpers.setSubmitting(false)
    }

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setValues,
        setFieldValue,
        resetForm,
        isSubmitting,
        isValid
    } = useFormik({
        initialValues: EMPTY_FORM,
        onSubmit,
        validationSchema: BillabilityUtilizationTargetFormValidation
    })

    // Visible years, plus the target's own year when editing one whose year was hidden.
    const { options: fiscalYearOptions } = useFiscalYearOptions(editingTarget?.fiscalYear)

    const handleAddTarget = () => {
        dispatch(setEditingTarget(null))
        setValues(EMPTY_FORM, false)
        setShowFormModal(true)
    }

    const handleEditTarget = (target: BillabilityUtilizationTarget) => {
        dispatch(setEditingTarget(target))
        setValues({
            fiscalYear: target.fiscalYear,
            utilizationTarget: target.utilizationTarget,
            actualBillableTarget: target.actualBillableTarget,
            leadsTarget: target.leadsTarget ?? '',
            principalsTarget: target.principalsTarget ?? '',
        }, false)
        setShowFormModal(true)
    }

    const handleDeleteTarget = (target: BillabilityUtilizationTarget) => {
        setFocusedTarget(target)
        setIsDeleteModalOpen(true)
    }

    const handleCloseDeleteModal = async () => {
        setIsDeleteModalOpen(false)
        setFocusedTarget(null)
    }

    const confirmDeleteTarget = async () => {
        if (!focusedTarget) return
        setIsDeleting(true)
        const success = await deleteBillabilityUtilizationTargetRequest(focusedTarget.id)
        if (success) {
            dispatch(removeTarget(focusedTarget.id))
            handleCloseDeleteModal()
        }
        setIsDeleting(false)
    }

    const columns = React.useMemo<ColumnDef<BillabilityUtilizationTarget>[]>(() => [
        {
            header: "Fiscal Year",
            accessorKey: "fiscalYear",
            size: 120,
        },
        {
            header: "Utilization Target",
            accessorKey: "utilizationTarget",
            size: 160,
            cell: ({ row }) => fmtPct(row.original.utilizationTarget),
        },
        {
            header: "Leads Target",
            accessorKey: "leadsTarget",
            size: 130,
            cell: ({ row }) => row.original.leadsTarget === null || row.original.leadsTarget === undefined
                ? <span className="text-muted">-</span> : fmtPct(row.original.leadsTarget),
        },
        {
            header: "Principals Target",
            accessorKey: "principalsTarget",
            size: 140,
            cell: ({ row }) => row.original.principalsTarget === null || row.original.principalsTarget === undefined
                ? <span className="text-muted">-</span> : fmtPct(row.original.principalsTarget),
        },
        {
            header: "Billable Target",
            accessorKey: "actualBillableTarget",
            size: 200,
            cell: ({ row }) => <TableRowControlsContainer style={{ justifyContent: 'space-between' }}>
                {fmtPct(row.original.actualBillableTarget)}
                <div className='d-flex flex-row justify-content-end'>
                    <IconButton onClick={() => handleEditTarget(row.original)}><PencilIcon /></IconButton>
                    <IconButton onClick={() => handleDeleteTarget(row.original)}><TrashIcon /></IconButton>
                </div>
            </TableRowControlsContainer>
        }
    ], [])

    const table = useDataTable({
        columns,
        data: targets,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="Billability / Utilization Targets"
        table={table}
        columns={columns}
        isLoading={isLoading}
        actions={<>
            <ActionButton onClick={handleAddTarget}>Add <PlusIcon /></ActionButton>
            <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
        </>}
        modal={<>
            <Modal
                isOpen={showFormModal}
                onClose={handleCloseFormModal}
                title={editingTarget ? "Edit Target" : "Create Target"}
            >
                <BillabilityUtilizationTargetForm
                    edit={!!editingTarget}
                    fiscalYears={fiscalYearOptions}
                    setFieldValue={setFieldValue}
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    handleSubmit={handleSubmit}
                    isValid={isValid}
                    isSubmitting={isSubmitting}
                />
            </Modal>
            <AreYouSureModal
                isOpen={isDeleteModalOpen}
                onModalClose={handleCloseDeleteModal}
                modalTitle="Delete Target"
                subTitle="Are you sure you want to delete this target"
                handleAreYouSureAccept={confirmDeleteTarget}
                handleAreYouSureDecline={handleCloseDeleteModal}
                areYouSureacceptText={isDeleting ? 'Deleting...' : 'Delete'}
                areYouSuredeclineText="Cancel"
            />
        </>}
    />
}

export default BillabilityUtilizationTargetsPage
